import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Layout, theme, Grid, Button, Flex, Modal, Skeleton, Badge, ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// services
import authService from 'core/services/auth';
import { isRTL } from 'core/services/language';
import { RootState } from 'stores/store';

//components
import Icon from 'utils/icon';
import { capitalizeWords } from 'utils/capitalizeWords';
import LanguageButton from 'components/language-button/language-button';
import PageName from 'components/pageName/pageName';
import NotificationDrawer from 'components/notification/notificationDrawer';

// styles
import logo from 'assets/images/logo-dark.svg';
import styles from '../layout.module.scss';
import MainDashboardMenu from './components/menu';
import PlanModalComponent from 'core/layouts/main/components/planModal';
import AccountStatusComponent from './components/accountStatus';
import { onGetPlans } from 'core/services/plans';
import { onGetNotificationsCount } from './services/notificationsService';
import ContactModalComponent from './components/contactModal';

const { useBreakpoint } = Grid;

function MainDashboardLayout() {
	const screens = useBreakpoint();
	const navigate = useNavigate();
	const { t } = useTranslation();

	//global states
	const language = useSelector((state: RootState) => state.language);
	const admin = useSelector((state: RootState) => state.admin);
	const company = useSelector((state: RootState) => state.company);

	// local states
	const [collapsed, setCollapsed] = useState(true);
	const [showAccountInfo, setShowAccountInfo] = useState(false);

	const [plansExist, setPlansExist] = useState(false);
	const [plansOpen, setPlansOpen] = useState(false);

	const [contactModalOpen, setContactModalOpen] = useState(false);

	const [notificationsOpen, setNotificationsOpen] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const {
		token: { colorBgContainer },
	} = theme.useToken();

	// styles
	const siderStyle: React.CSSProperties = {
		overflowY: 'auto',
		overflowX: 'hidden',
		position: screens.sm ? 'sticky' : 'fixed',
		insetInlineStart: 0,
		top: 0,
		bottom: 0,
		scrollbarWidth: 'thin',
		zIndex: 99,
		background: colorBgContainer,
		height: !screens.sm ? 'unset' : '100vh',
	};

	const headerStyle: React.CSSProperties = {
		padding: '0px',
		background: colorBgContainer,
		position: 'sticky',
		top: 0,
		borderBottom: '1px solid #eee',
		paddingInline: '.9rem',
		zIndex: 100,
	};

	useEffect(() => {
		getPlansHandler();
		getUnreadCountHandler();
	}, []);

	useEffect(() => {
		if (!screens.sm && !collapsed) document.body.style.overflowY = 'hidden';
		if (!screens.sm && collapsed) document.body.style.overflowY = 'auto';
	}, [collapsed]);

	// handlers
	const getPlansHandler = async () => {
		try {
			const response = await onGetPlans();
			if (response.plans.length > 0) setPlansExist(true);
		} catch (error) {}
	};

	const getUnreadCountHandler = async () => {
		try {
			if (!company?.has_active_subscription) return;
			
			const response = await onGetNotificationsCount();
			setUnreadCount(response.unread_count);
		} catch (error) {}
	};

	const onChangeCollapsible = () => {
		setCollapsed(!collapsed);
	};

	const showAccountHandler = () => {
		if (collapsed) {
			setShowAccountInfo(!showAccountInfo);
		}
	};

	const onWalletClick = () => {
		if (window.innerWidth <= 576) setCollapsed(true);
		if (company?.usage_start_at) navigate('wallet');
	};

	const onLogout = () => {
		authService.clearAuthData();
		navigate('/');
	};

	return (
		<>
			<Layout hasSider style={{ minHeight: '100vh' }}>
				{!screens.sm && !collapsed && <div className={styles.siderBack} onClick={onChangeCollapsible}></div>}

				<Layout.Sider
					style={siderStyle}
					breakpoint="lg"
					collapsedWidth={screens.md && !collapsed ? 300 : screens.sm ? 70 : 0}
					width={300}
					collapsed={collapsed}
					onCollapse={(value) => setCollapsed(value)}
					trigger={null}>
					{screens.lg && (
						<Button
							size="small"
							color="default"
							variant="filled"
							className={styles.collapseBtn}
							onClick={onChangeCollapsible}
							style={{ insetInlineStart: collapsed ? 53 : 283, transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}>
							<Icon name="leftArrow" color="#2884EF" style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }} />
						</Button>
					)}

					<div>
						<img
							src={logo}
							alt="logo"
							style={{
								width: 140,
								margin: 20,
								marginInlineStart: 30,
								transition: 'all .2s ease-in-out',
								transform: `translateX(${
									screens.sm && collapsed && language === 'en' ? '-107px' : screens.sm && collapsed && language === 'ar' ? '23px' : '0px'
								})`,
							}}
						/>

						<MainDashboardMenu collapsed={collapsed} changeCollapsed={setCollapsed} />
					</div>

					<Flex gap={10} align="center" justify="space-between" className={styles.account}>
						<Flex align="center" gap={10} style={{ minWidth: 0 }}>
							<Button type="link" className="p-0" onMouseEnter={showAccountHandler} onMouseLeave={() => setShowAccountInfo(false)}>
								<img src={company?.company_logo} />
								{showAccountInfo && (
									<Flex gap={20} align="center" className={`${styles.collapsedAccountInfo} ${styles[language]}`}>
										<div>
											<h6 className="fw-semibold">{company?.company_name}</h6>
											<span style={{ direction: 'ltr' }}>{admin?.email}</span>
										</div>
										<Button type="link" className="p-0" onClick={onLogout}>
											<Icon
												name="logout"
												width={30}
												height={30}
												color="var(--primary)"
												style={{ transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}
											/>
										</Button>
									</Flex>
								)}
							</Button>
							{!collapsed && (
								<div className={styles.account_data}>
									<h6 className="fw-semibold">{company?.company_name}</h6>
									<span style={{ direction: 'ltr' }}>{admin?.email}</span>
								</div>
							)}
						</Flex>
						{!collapsed && (
							<Button type="link" className="p-0" onClick={onLogout}>
								<Icon
									name="logout"
									width={30}
									height={30}
									color="var(--primary)"
									style={{ transform: language == 'en' ? 'rotate(0deg)' : 'rotate(180deg)' }}
								/>
							</Button>
						)}
					</Flex>
				</Layout.Sider>

				<Layout style={{ background: colorBgContainer }}>
					<Layout.Header style={headerStyle} className={styles.navHeader}>
						<img src={logo} alt="logo" className="d-sm-none" style={{ width: 120 }} />

						<Flex align="center" gap={10}>
							<Flex align="center" gap={10} className="d-none d-sm-flex">
								{company?.usage_start_at && company?.profile_status !== 'GeneralSuspended' ? (
									<Button
										color="default"
										variant="filled"
										size="large"
										className="text-middle"
										style={{
											cursor: plansExist ? 'pointer' : 'default',
										}}
										onClick={() => plansExist && setPlansOpen(true)}>
										<Icon name="plan" width={16} height={16} color="#888" />
										<p className="mb-0 fw-medium text-light">
											{t('COMMON.PLAN')}:{' '}
											<span className="text-success fw-semibold">
												{company?.has_active_subscription
													? isRTL()
														? company?.active_subscription?.translated_plan.ar
														: company?.active_subscription?.translated_plan.en
													: t('COMMON.FREE_PLAN')}
											</span>
										</p>
									</Button>
								) : (
									<AccountStatusComponent />
								)}

								{company?.usage_start_at && (
									<Button color="default" variant="filled" size="large" className="text-middle" onClick={onWalletClick}>
										<Icon name="wallet" color="#888" />
										<p className="mb-0 fw-medium text-light">
											{t('COMMON.WALLET')}:{' '}
											<span className="text-success fw-semibold">
												{(company?.point_wallet ?? 0).toLocaleString('en-US')} {t('COMMON.POINTS')}
											</span>
										</p>
									</Button>
								)}
							</Flex>

							<Flex align="center" gap={10}>
								<ConfigProvider theme={{ token: { motion: false } }}>
									<Badge count={unreadCount} size="small" offset={[isRTL() ? 4 : -4, 4]} style={{ fontSize: 10 }}>
										<Button
											color={unreadCount > 0 ? 'blue' : 'default'}
											variant="filled"
											className={styles.notificationBtn}
											onClick={() => setNotificationsOpen(true)}>
											<Icon name="notifications" width={14} height={18} color={unreadCount > 0 ? 'var(--primary)' : '#888'} />
										</Button>
									</Badge>
								</ConfigProvider>

								<div className="order-0 order-md-1">
									<LanguageButton />
								</div>
							</Flex>

							<div style={{ transform: 'rotate(180deg)' }}>
								<Button type="link" className={`${styles.menuToggler} ${!collapsed && styles.active}`} onClick={onChangeCollapsible}>
									<span></span>
									<span></span>
									<span></span>
								</Button>
							</div>
						</Flex>
					</Layout.Header>

					<Layout.Content className={styles.content}>
						<div>
							<h4 className="fw-semibold">
								{t('COMMON.WELCOME')}
								{isRTL() ? '، ' : ', '}
								{isRTL() ? admin?.translated_name.ar.split(' ')[0] : capitalizeWords(admin?.name.split(' ')[0] ?? admin?.translated_name.ar.split(' ')[0])}
							</h4>
							<PageName />
						</div>

						<Outlet />
					</Layout.Content>
				</Layout>
			</Layout>

			<PlanModalComponent open={plansOpen} onClose={() => setPlansOpen(false)} currentPlanKey={company?.active_subscription?.key} />

			<ContactModalComponent open={contactModalOpen} onClose={() => setContactModalOpen(false)} />

			{admin && (
				<NotificationDrawer
					userCompanyId={admin.id}
					open={notificationsOpen}
					onClose={() => setNotificationsOpen(false)}
					hasActiveSubscription={company?.has_active_subscription!}
					unreadCount={unreadCount}
					onUnreadCountChange={setUnreadCount}
				/>
			)}
		</>
	);
}

export default MainDashboardLayout;
